import { useState, useRef, RefObject } from "react";
import {
  Button,
  Modal,
  DateInput,
  TextAreaInput,
  Grid,
  styled,
} from "@monolith-forensics/monolith-ui";
import { useForm } from "@mantine/form";
import { z } from "zod";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import ButtonMenu from "@/components/Forms/components/ButtonMenu.js";

import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import { getIpAddress } from "@/api";
import { db_timestamp } from "@/utils/date-format";

import type { BaseCocModalProps, CocSignatureObject } from "../Coc.types";
import EvidenceCocAPI from "@/api/evidence/coc";
import { createSignature } from "../utils";
import { SignatureField, SignatureFieldRef } from "@/Features";

const DescriptionText = styled.div`
  padding-bottom: 20px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

interface ReturnToParentCocModalProps extends BaseCocModalProps {}

interface ReturnToParentFormData {
  reason: string;
  timestamp: string;
}

const returnToParentValidationSchema = z.object({
  reason: z
    .string({
      required_error: "Reason is required",
    })
    .min(1, { message: "Reason is required" }),
  timestamp: z
    .string({
      required_error: "Timestamp is required",
    })
    .min(1, { message: "Timestamp is required" }),
});

const ReturnToParentCocModal: React.FC<ReturnToParentCocModalProps> = ({
  show,
  onClose,
  onSubmit,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<ReturnToParentFormData>({
    mode: "uncontrolled",
    initialValues: {
      reason: "",
      timestamp: new Date().toISOString(),
    },
  });

  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const checkoutFromSig = useRef<SignatureFieldRef>();

  const handleSubmit = async () => {
    const formData = form.getValues();

    const validationResult = await validateAsync(
      returnToParentValidationSchema
    )(formData);
    if (validationResult?.hasErrors) {
      console.error("Validation Errors", validationResult?.errors);
      form.setErrors(validationResult?.errors);
      return;
    }

    setSubmitting(true);
    try {
      const ipAddress = await getIpAddress();

      const signature: CocSignatureObject = {
        custody_from_sig: createSignature(
          checkoutFromSig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
      };

      const requestObj = {
        caseInfo: {
          case_id: defaultInfo.case_id,
          case_number: defaultInfo.case_number,
          case_ref: defaultInfo.case_ref,
        },
        evidenceInfo: {
          evidence_id: defaultInfo.evidence_id,
          uuid: defaultInfo.uuid,
          evidence_number: defaultInfo.evidence_number,
        },
        moveData: {
          entered_by_id: currentUser.user_id,
          entered_by: currentUser.full_name,
          new_location_id: 0,
          new_location_name: `Parent Evidence (${defaultInfo.parent_name})`,
          current_location_name: defaultInfo.location_name,
          reason: formData.reason || "Evidence returned to parent",
          timestamp: db_timestamp(formData.timestamp, true),
          signature: JSON.stringify(signature),
          coc_type: "Move",
        },
      };

      await EvidenceCocAPI.moveEvidence(requestObj);

      enqueueSnackbar(`Evidence item returned to parent`, {
        variant: "success",
      });

      onSubmit?.(requestObj);
    } catch (error) {
      console.error("Error returning evidence to parent:", error);
      enqueueSnackbar(`Error returning evidence item to parent.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose?.();
  };

  return (
    <Modal open={show} onClose={onClose} style={{ width: 600 }}>
      <Modal.Title>Return Evidence Item to Parent</Modal.Title>
      <div style={{ margin: "20px 0px" }}>
        <DescriptionText>
          Are you sure you wish to return this item to its parent evidence? The
          item will be moved to the parent and a CoC record will be created.
        </DescriptionText>
        <Grid col={1}>
          <TextAreaInput
            label="Reason"
            required
            minRows={3}
            placeholder="Enter reason for returning to parent..."
            key={form.key("reason")}
            {...form.getInputProps("reason")}
          />
          <DateInput
            label="Timestamp"
            required
            format="YYYY-MM-DD HH:mm:ss"
            enableCalendar
            arrow
            key={form.key("timestamp")}
            {...form.getInputProps("timestamp")}
          />
          <div>
            <SignatureField
              sigFieldRef={checkoutFromSig}
              label="Signature"
              useESignature={true}
            />
          </div>
        </Grid>
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Return to Parent
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

export default ReturnToParentCocModal;
